import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  InputProps,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { MapPin } from '@phosphor-icons/react';
import { useAppdata, useTranslation } from '../lib/hooks.context';
import { MhyChakraProps, Site } from '../types';
import AutoSuggest, { Option } from './AutoSuggest';
import { CloseIcon } from '../icons';
import { arrayOrStringToArray } from '../lib/utils';

/*
 * SiteSearch - Provides search field to find site and submits found result to URL params
 * SiteSelect - Provides search field to find site and takes 'onAdd' callback
 * SiteSelector - Provides search field to find site and provides link to it
 * AssociationSearch - Does pretty much the same as SiteSelector
 */

const sanitizeSites = (sites: Site[], claimedPaths: string[]) =>
  sites?.filter(({ path }) =>
    claimedPaths.includes(path.replace(/\//g, '') || 'main'),
  );

const sanitizeSitePaths = (sites: Site[], claimedPaths: string[]) =>
  claimedPaths?.filter((claimed) =>
    sites.find(({ path }) => (path.replace(/\//g, '') || 'main') === claimed),
  );

const getSites = (
  queriedSites: string | string[],
  subsite: string | undefined | null,
  sites: Site[],
) => {
  if (!sites) {
    return [];
  }
  const claimedSitePaths = [
    ...(subsite ? [subsite, 'main'] : []),
    ...arrayOrStringToArray(queriedSites),
  ];

  return sanitizeSites(sites, claimedSitePaths);
};

const updateSitesQueryString = (
  queriedSites: string | string[],
  { value }: Option,
  sites: Site[],
  currentSubsite?: string | null,
): string => {
  const existing = sanitizeSitePaths(sites, arrayOrStringToArray(queriedSites));

  return (
    existing.find((val) => val === value)
      ? existing.filter((val) => val !== value)
      : [...existing, value]
  )
    .filter((s) => s !== currentSubsite)
    .join(',');
};

const siteToOption = ({
  path,
  blog_id: id,
  blogname: name,
  tags,
}: Site): Option => ({
  value: path.replace(/\//g, '') || 'main',
  id,
  name,
  tags,
  node: () => (
    <Text as="span" py="2" px="1">
      {name}
    </Text>
  ),
});

const mapSitesToOptions = (sites: Site[]) => sites?.map(siteToOption);

const SiteSearch = ({
  isLoading,
  justifyContent,
  size = 'normal',
  ...rest
}: Omit<MhyChakraProps, 'variant'> & { isLoading?: boolean } & Pick<
    InputProps,
    'size'
  >) => {
  const { sites, subsite } = useAppdata();
  const {
    query: { sites: querySites = [] },
    push,
    asPath,
  } = useRouter();
  const t = useTranslation();
  const currentOptions = mapSitesToOptions(
    getSites(querySites, subsite, sites),
  ).sort(({ value }) => (value === (subsite || 'main') ? -1 : 0));

  const updateSitesQuery = (sitesQuery: string) => {
    push(
      {
        pathname: asPath.split('?')[0],
        query: sitesQuery
          ? {
              sites: sitesQuery,
            }
          : {},
      },
      undefined,
      {
        shallow: true,
      },
    );
  };

  const onAdd = (option: Option) => {
    updateSitesQuery(
      updateSitesQueryString(querySites, option, sites, subsite),
    );
  };

  const onRemove = (option: Option) => {
    const updatedQs = updateSitesQueryString(
      querySites,
      option,
      sites,
      subsite,
    );

    updateSitesQuery(updatedQs);
  };

  return (
    <Box {...rest} justifyContent={justifyContent}>
      <AutoSuggest
        items={mapSitesToOptions(sites)}
        selectedItems={currentOptions}
        onAdd={onAdd}
        onRemove={onRemove}
      >
        {({ getInputProps, getOuterProps }) => (
          <InputGroup {...getOuterProps()} flexWrap="wrap" size={size}>
            <InputLeftElement pointerEvents="none" color="highlightColor">
              <MapPin size="18" />
            </InputLeftElement>
            <Input
              variant="search"
              pr="8"
              pl="10"
              {...getInputProps({
                id: 'sites-search',
                placeholder: t('search_association_placeholder'),
              })}
            />
            {!isLoading ? null : (
              <InputRightElement>
                <Button as="span" variant="transparent" isLoading />
              </InputRightElement>
            )}
          </InputGroup>
        )}
      </AutoSuggest>
      <Flex
        flexWrap="wrap"
        pt="2"
        justifyContent={justifyContent}
        gridColumnGap="3"
        gridRowGap="2"
      >
        {currentOptions.map((selectedItem, index) =>
          (!index && subsite) ||
          (subsite && selectedItem.value === 'main') ? null : (
            <Button
              key={`selected-item-${index}`}
              variant="tag"
              onClick={() => {
                onRemove(selectedItem);
              }}
            >
              {selectedItem.name}
              <CloseIcon size="3" />
            </Button>
          ),
        )}
      </Flex>
    </Box>
  );
};

export default SiteSearch;
